import React, { useRef } from "react";
import { X, Printer } from "lucide-react";

// Helper function to format time (e.g., "11:37 PM")
const formatTime = (dateString) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

// Helper function to format date (e.g., "03/24/2026")
const formatShortDate = (dateString) => {
  if (!dateString) return "";
  const d = new Date(dateString);
  return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`;
};

export const ShipmentTripTicketModal = ({ isOpen, onClose, data }) => {
  const printRef = useRef();

  if (!isOpen || !data) return null;

  // const handlePrint = () => {
  //   const printContent = printRef.current.innerHTML;
  //   const originalContent = document.body.innerHTML;

  //   // Apply print specific styles
  //   document.body.innerHTML = `
  //     <style>
  //       @page { size: auto; margin: 0mm; }
  //       body { margin: 20px; font-family: Arial, sans-serif; -webkit-print-color-adjust: exact; }
  //     </style>
  //     ${printContent}
  //   `;
  //   window.print();
  //   document.body.innerHTML = originalContent;
  //   window.location.reload(); // Reload to restore React state cleanly
  // };

  const handlePrint = () => {
    const printContent = printRef.current.innerHTML;
    const originalContent = document.body.innerHTML;

    document.body.innerHTML = `
      <style>
        @page {
          size: 800px 1100px portrait;
          margin: 0;
        }

        html, body {
          width: 800px;
          min-height: 1100px;
          margin: 0;
          padding: 0;
        }

        body {
          box-sizing: border-box;
          padding: 20px;
          font-family: Arial, sans-serif;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }

        *, *::before, *::after {
          box-sizing: border-box;
        }

        @media print {
          body {
            width: 800px;
            min-height: 1100px;
          }
        }
      </style>

      <div style="width: 760px; min-height: 1060px;">
        ${printContent}
      </div>
    `;

    window.print();

    document.body.innerHTML = originalContent;
    window.location.reload();
  };

  // Fallbacks to match the image exactly if data is missing
  const customerName = data.customerName || "";
  const tripTicketNumber = data.tripTicketNumber || "";
  const date = formatShortDate(data.plannedDate) || "";
  const shipTo = data.locations || [];
  const plate = data.plateNumber || "";
  const plantDepart = formatTime(data.startedAt) || "";
  const siteArrive = formatTime(data.siteArrivedAt) || "";
  const siteDepart = formatTime(data.siteDepartedAt) || "";
  const plantArrive = formatTime(data.completedAt) || "";
  const preparedBy = data.preparedByName || "";
  const driverName = data.driverName || "";

  const items =
    data.items && data.items.length > 0
      ? data.items
      : [
          {
            qty: 4,
            uom: "cubic",
            productName: "RMC G1 Ordinary 3000PSI 7Days",
          },
        ];

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-900/70 backdrop-blur-sm p-4">
      {/* Modal Container */}
      <div className="bg-gray-100 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[95vh] flex flex-col overflow-hidden relative">
        {/* Modal Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10 shrink-0">
          <h3 className="font-bold text-gray-800 text-lg flex items-center gap-2">
            <Printer size={20} className="text-indigo-600" /> Print Trip Ticket
          </h3>
          <div className="flex gap-3">
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-indigo-700 transition"
            >
              <Printer size={16} /> Print Document
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 rounded-lg transition"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Printable Area Wrapper (Scrollable) */}
        <div className="p-8 overflow-y-auto flex-grow flex justify-center custom-scrollbar">
          {/* 📄 ACTUAL PAPER COMPONENT */}
          <div
            ref={printRef}
            className="bg-white shadow-md p-10 w-full max-w-[800px] text-black font-sans box-border"
            style={{ minHeight: "800px" }}
          >
            {/* Top Right: Trip # */}
            <div className="flex justify-end text-[17px] font-bold mb-4 pr-10">
              <span>Trip # : {tripTicketNumber}</span>
            </div>

            {/* Title & Date */}
            <div className="flex justify-between items-end mb-8 pl-10 pr-4">
              <h1 className="text-[22px] font-bold tracking-wide">
                {customerName}
              </h1>
              <span className="text-[17px] font-bold">{date}</span>
            </div>

            {/* Details Section */}
            <div className="px-2 mb-6">
              {/* Row 1: Ship to & Plate # */}
              <div className="flex justify-between items-start mb-4"> {/* Changed items-end to items-start for multi-line support */}
                <div className="flex items-start w-[65%]">
                  <span className="font-bold text-[15px] mr-3 whitespace-nowrap mt-0.5">
                    Ship to:
                  </span>
                  <div className="border-b border-black flex-grow pb-0.5 text-[14px] uppercase leading-tight">
                    {/* Dynamic mapping of locations */}
                    {shipTo && shipTo.length > 0 ? (
                      shipTo.map((loc, index) => (
                        <span key={loc.id}>
                          {loc.locationName}
                          {index < shipTo.length - 1 ? ", " : ""}
                        </span>
                      ))
                    ) : (
                      <span className="text-gray-400">No Location Assigned</span>
                    )}
                  </div>
                </div>
                
                <div className="flex items-end justify-end w-[30%]">
                  <span className="font-bold text-[15px] mr-4">Plate # :</span>
                  <span className="text-[15px] mr-6">{plate}</span>
                </div>
              </div>

              {/* Row 2: Plant Depart & Site Depart */}
              <div className="flex justify-between items-end mb-3">
                <div className="flex items-end w-[48%]">
                  <span className="font-bold text-[15px] mr-3 whitespace-nowrap">
                    Plant Depart :
                  </span>
                  <div className="border-b border-black flex-grow text-center pb-0.5 text-[15px]">
                    {plantDepart}
                  </div>
                </div>
                <div className="flex items-end w-[48%]">
                  <span className="font-bold text-[15px] mr-3 whitespace-nowrap">
                    Site Depart :
                  </span>
                  <div className="border-b border-black flex-grow text-center pb-0.5 text-[15px]">
                    {siteDepart}
                  </div>
                </div>
              </div>

              {/* Row 3: Site Arrive & Plant Arrive */}
              <div className="flex justify-between items-end mb-2">
                <div className="flex items-end w-[48%]">
                  <span className="font-bold text-[15px] mr-3 whitespace-nowrap">
                    Site Arrive :
                  </span>
                  <div className="border-b border-black flex-grow text-center pb-0.5 text-[15px]">
                    {siteArrive}
                  </div>
                </div>
                <div className="flex items-end w-[48%]">
                  <span className="font-bold text-[15px] mr-3 whitespace-nowrap">
                    Plant Arrive :
                  </span>
                  <div className="border-b border-black flex-grow text-center pb-0.5 text-[15px]">
                    {plantArrive}
                  </div>
                </div>
              </div>
            </div>

            {/* Items Table */}
            <table className="w-full border-collapse border border-black mb-16 text-[15px]">
              <thead>
                <tr className="border-b border-black">
                  <th className="border-r border-black p-1.5 w-[12%] text-center font-bold">
                    Qty.
                  </th>
                  <th className="border-r border-black p-1.5 w-[15%] text-center font-bold">
                    U/M
                  </th>
                  <th className="p-1.5 w-[73%] text-center font-bold">
                    Particulars
                  </th>
                </tr>
              </thead>
              <tbody>
                {/* 
                  Applying a large height to the first row to simulate the 
                  blank space shown in the physical receipt image 
                */}
                {items.map((item, idx) => (
                  <tr
                    key={idx}
                    className={`align-top`}
                  >
                    <td className="border-r border-black p-2 text-center">
                      {item.qty}
                    </td>
                    <td className="border-r border-black p-2 text-center">
                      {item.uom}
                    </td>
                    <td className="p-2">{`${item.productName} ${item.brandName ? `(${item.brandName})` : ``}`}</td>
                  </tr>
                ))}
                {/* Fallback empty row if no items exist to keep layout height */}
                {items.length === 0 && (
                  <tr className="h-[320px] align-top">
                    <td className="border-r border-black p-2"></td>
                    <td className="border-r border-black p-2"></td>
                    <td className="p-2"></td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Signatures Footer */}
            <div className="flex justify-between px-6 text-[15px] mt-auto">
              {/* Prepared By */}
              <div className="flex flex-col items-center w-[28%]">
                <span className="mb-1">{preparedBy}</span>
                <div className="border-t border-black w-full text-center pt-1">
                  Prepared by
                </div>
              </div>

              {/* Driver */}
              <div className="flex flex-col items-center w-[28%]">
                <span className="mb-1">{driverName}</span>
                <div className="border-t border-black w-full text-center pt-1">
                  Driver
                </div>
              </div>

              {/* Received By */}
              <div className="flex flex-col items-center w-[28%] mt-[22px]">
                {" "}
                {/* mt applied to match baseline */}
                <div className="border-t border-black w-full text-center pt-1">
                  Received by
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
